import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Field } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROUNDING_LABELS = {
  none: "Tanpa pembulatan", "1000": "Rp1.000", "5000": "Rp5.000", "10000": "Rp10.000",
  "25000": "Rp25.000", "50000": "Rp50.000", custom: "Kustom",
};

// Reusable pricing calculator. props: value(object), onChange, exchangeRate, compact
export default function PricingCalculator({ value, onChange, exchangeRate }) {
  const [breakdown, setBreakdown] = useState(null);
  const v = value;

  const set = (k, val) => onChange({ ...v, [k]: val });

  const compute = useCallback(async () => {
    try {
      const r = await api.post("/pricing/calculate", {
        aud_price: String(v.aud_price || "0"),
        exchange_rate: String(exchangeRate || "11000"),
        margin_percent: String(v.margin_percent ?? "0"),
        jasa_titip_type: v.jasa_titip_type || "fixed",
        jasa_titip_value: String(v.jasa_titip_value ?? "0"),
        delivery_fee: String(v.delivery_fee ?? "0"),
        additional_fee: String(v.additional_fee ?? "0"),
        discount: String(v.discount ?? "0"),
        rounding_method: v.rounding_method || "none",
        custom_step: Number(v.custom_step || 0),
        quantity: Number(v.quantity || 1),
      });
      setBreakdown(r.data);
    } catch (e) {
      console.error("Gagal menghitung harga:", e);
    }
  }, [v, exchangeRate]);

  useEffect(() => { compute(); }, [compute]);

  const Row = ({ label, val, strong, muted }) => (
    <div className={`flex items-center justify-between py-1.5 ${strong ? "border-t border-border mt-1 pt-2" : ""}`}>
      <span className={`text-sm ${muted ? "text-muted-foreground" : ""}`}>{label}</span>
      <span className={`font-mono tabular-nums text-sm ${strong ? "font-semibold" : ""}`}>{val}</span>
    </div>
  );

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-3">
        <Field label="Harga AUD">
          <Input type="number" step="0.01" value={v.aud_price ?? ""} onChange={(e) => set("aud_price", e.target.value)}
            data-testid="pricing-aud-input" placeholder="25.00" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Margin (%)">
            <Input type="number" value={v.margin_percent ?? ""} onChange={(e) => set("margin_percent", e.target.value)} data-testid="pricing-margin-input" />
          </Field>
          <Field label="Kurs AUD→IDR">
            <Input value={exchangeRate} disabled className="font-mono" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipe Jasa Titip">
            <Select value={v.jasa_titip_type || "fixed"} onValueChange={(val) => set("jasa_titip_type", val)}>
              <SelectTrigger data-testid="pricing-jasa-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Nominal Tetap</SelectItem>
                <SelectItem value="percentage">Persentase</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={v.jasa_titip_type === "percentage" ? "Jasa Titip (%)" : "Jasa Titip (Rp)"}>
            <Input type="number" value={v.jasa_titip_value ?? ""} onChange={(e) => set("jasa_titip_value", e.target.value)} data-testid="pricing-jasa-value" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ongkir (Rp)">
            <Input type="number" value={v.delivery_fee ?? ""} onChange={(e) => set("delivery_fee", e.target.value)} data-testid="pricing-delivery" />
          </Field>
          <Field label="Biaya Lain (Rp)">
            <Input type="number" value={v.additional_fee ?? ""} onChange={(e) => set("additional_fee", e.target.value)} data-testid="pricing-additional" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Diskon (Rp)">
            <Input type="number" value={v.discount ?? ""} onChange={(e) => set("discount", e.target.value)} data-testid="pricing-discount" />
          </Field>
          <Field label="Pembulatan">
            <Select value={v.rounding_method || "none"} onValueChange={(val) => set("rounding_method", val)}>
              <SelectTrigger data-testid="pricing-rounding"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROUNDING_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {v.rounding_method === "custom" && (
          <Field label="Kelipatan Kustom (Rp)">
            <Input type="number" value={v.custom_step ?? ""} onChange={(e) => set("custom_step", e.target.value)} data-testid="pricing-custom-step" />
          </Field>
        )}
      </div>

      <div className="rounded-xl border border-border bg-secondary/30 p-4">
        <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-2">Rincian Harga</p>
        {breakdown && (
          <div>
            <Row label="Harga AUD" val={`AUD ${breakdown.aud_price}`} muted />
            <Row label="Kurs diterapkan" val={breakdown.exchange_rate} muted />
            <Row label="Harga IDR (konversi)" val={formatIDR(breakdown.purchase_idr)} />
            <Row label={`Margin ${breakdown.margin_percent}%`} val={formatIDR(breakdown.margin_amount)} />
            <Row label="Jasa titip" val={formatIDR(breakdown.jasa_titip_fee)} />
            <Row label="Ongkir" val={formatIDR(breakdown.delivery_fee)} />
            <Row label="Biaya lain" val={formatIDR(breakdown.additional_fee)} />
            <Row label="Diskon" val={`- ${formatIDR(breakdown.discount)}`} />
            <Row label="Sebelum pembulatan" val={formatIDR(breakdown.price_before_rounding)} muted />
            <Row label="Penyesuaian pembulatan" val={formatIDR(breakdown.rounding_adjustment)} muted />
            <div className="flex items-center justify-between border-t border-border mt-2 pt-3">
              <span className="text-sm font-medium">Harga Jual Final</span>
              <span className="font-heading text-2xl font-semibold text-primary" data-testid="pricing-final-idr">{formatIDR(breakdown.final_price)}</span>
            </div>
            {Number(v.quantity || 1) > 1 && (
              <Row label={`Total (${v.quantity} pcs)`} val={formatIDR(breakdown.line_total)} strong />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
